<template>
  <Teleport to="body">
    <div
      v-if="product"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0"
      >
        <!-- Background overlay -->
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          @click="$emit('close')"
        ></div>

        <!-- Modal panel -->
        <div
          class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
        >
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div
                v-if="product.featured_image_url"
                class="mx-auto flex-shrink-0 flex items-center justify-center h-24 w-24 rounded-md overflow-hidden bg-gray-100 sm:mx-0 sm:h-32 sm:w-32"
              >
                <img
                  :src="product.featured_image_url"
                  :alt="product.title"
                  class="w-full h-full object-cover"
                />
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                <h3
                  id="modal-title"
                  class="text-lg leading-6 font-bold text-custom-text"
                >
                  {{ product.title }}
                </h3>
                <div class="mt-2">
                  <p
                    v-if="product.description"
                    class="text-sm text-custom-grey-1 whitespace-pre-wrap"
                    v-html="product.description"
                  ></p>
                  <p v-else class="text-sm text-custom-grey-1 italic">
                    {{ t("no_description") }}
                  </p>
                </div>
                <div class="mt-4">
                  <span class="font-bold text-custom-text text-lg">
                    {{ n(product.price_in_cents / 100, "currency") }}
                  </span>
                  <span class="text-sm text-custom-grey-1 ml-1">
                    {{ t(`vat.${product.vat}`) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div
            class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2"
          >
            <button
              type="button"
              @click="$emit('toggle', product)"
              class="w-full inline-flex justify-center rounded-md px-4 py-2 text-sm font-semibold sm:w-auto transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              :class="
                isInOrder
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-primary text-white hover:bg-primary-700'
              "
              :disabled="saving"
            >
              <span v-if="saving">
                {{ t("saving") }}
              </span>
              <span v-else-if="isInOrder">
                {{ t("remove") }}
              </span>
              <span v-else>
                {{ t("add_to_order") }}
              </span>
            </button>
            <button
              type="button"
              @click="$emit('close')"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 bg-white text-sm font-medium text-custom-grey-1 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 sm:mt-0 sm:w-auto transition-colors duration-200"
            >
              {{ t("close") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import type { IProduct } from "@/scripts/application/types";
import { useI18n } from "vue-i18n";

defineProps<{
  product: IProduct | null;
  isInOrder: boolean;
  saving?: boolean;
}>();

defineEmits<{
  (event: "close"): void;
  (event: "toggle", product: IProduct): void;
}>();

const { n, t } = useI18n({ inheritLocale: true, useScope: "local" });
</script>

<i18n lang="yaml">
en:
  add_to_order: "Add to order"
  remove: "Remove"
  saving: "Saving..."
  close: "Close"
  no_description: "No description available"
  vat:
    unset: "incl. VAT"
    default_vat: "incl. VAT"
    zero_vat: "excl. VAT"
    no_vat: "excl. VAT"
nb:
  add_to_order: "Legg til"
  remove: "Fjern"
  saving: "Lagrer..."
  close: "Lukk"
  no_description: "Ingen beskrivelse tilgjengelig"
  vat:
    unset: "inkl. MVA"
    default_vat: "inkl. MVA"
    zero_vat: "ekskl. MVA"
    no_vat: "ekskl. MVA"
uk:
  add_to_order: "Додати"
  remove: "Видалити"
  saving: "Збереження..."
  close: "Закрити"
  no_description: "Опис відсутній"
  vat:
    unset: "з ПДВ"
    default_vat: "з ПДВ"
    zero_vat: "без ПДВ"
    no_vat: "без ПДВ"
es:
  add_to_order: "Añadir"
  remove: "Eliminar"
  saving: "Guardando..."
  close: "Cerrar"
  no_description: "Sin descripción disponible"
  vat:
    unset: "IVA incluido"
    default_vat: "IVA incluido"
    zero_vat: "sin IVA"
    no_vat: "sin IVA"
pl:
  add_to_order: "Dodaj"
  remove: "Usuń"
  saving: "Zapisywanie..."
  close: "Zamknij"
  no_description: "Brak opisu"
  vat:
    unset: "z VAT"
    default_vat: "z VAT"
    zero_vat: "bez VAT"
    no_vat: "bez VAT"
</i18n>

